import { safeNumber } from 'helper/common'
import {
  PERIOD_OPTIONS,
  REBATE_CONDITIONS_TYPE
} from 'features/ContractDetails/RebateConditions/RebateConditionsForm/constants'
import {
  MARKET_SHARE_THRESHOLD_MEASURED_IN,
  PURCHASE_VOLUME_THRESHOLD_MEASURED_IN,
  THRESHOLD_LESS_THAN_PREVIOUS_TIER
} from 'features/ContractDetails/RebateConditions/RebateConditionsForm/MarketShareRebateCondition/constants'
import {
  RebateTier,
  RebateTierRequest
} from 'features/ContractDetails/RebateConditions/RebateConditionsForm/MarketShareRebateCondition/types'
import { IContractDetails } from 'features/ContractDetails/types'
import {
  IRebateCondition,
  IRebateConditionForm,
  RebateDataRequest
} from 'features/ContractDetails/RebateConditions/RebateConditionsForm/types'

export const getMarketShareThresholdFieldName = (
  thresholdMeasuredIn?: string
): 'market_share_threshold' | 'purchase_volume_threshold' =>
  thresholdMeasuredIn === PURCHASE_VOLUME_THRESHOLD_MEASURED_IN
    ? 'purchase_volume_threshold'
    : 'market_share_threshold'

export const convertDetailsToForm = (
  details: IContractDetails
): IRebateConditionForm => {
  let rebate: IRebateCondition = details.rebate ? { ...details.rebate } : null

  if (details.rebate_type === REBATE_CONDITIONS_TYPE.NO_REBATE) {
    rebate = null
  }

  if (details.rebate_type === REBATE_CONDITIONS_TYPE.STRAIGHT_REBATE) {
    rebate = {
      amount: details.rebate?.amount ? +details.rebate.amount : null,
      period_threshold: details.rebate?.period_threshold
    }
  }

  if (details.rebate_type === REBATE_CONDITIONS_TYPE.GROWTH_REBATE) {
    rebate = {
      amount: details.rebate?.amount ? +details.rebate.amount : null,
      purchase_volume_threshold: details.rebate?.purchase_volume_threshold
        ? +details.rebate.purchase_volume_threshold
        : null,
      period_threshold: details.rebate?.period_threshold
    }
  }

  if (details.rebate_type === REBATE_CONDITIONS_TYPE.MARKET_SHARE_REBATE) {
    const thresholdMeasuredIn =
      details.rebate?.threshold_measured_in ??
      MARKET_SHARE_THRESHOLD_MEASURED_IN
    const thresholdField = getMarketShareThresholdFieldName(thresholdMeasuredIn)
    const thresholdValue = details.rebate?.[thresholdField]
      ? +(details.rebate?.[thresholdField] ?? 0)
      : null

    rebate = {
      amount: details.rebate?.amount ? +details.rebate.amount : null,
      market_share_threshold:
        thresholdField === 'market_share_threshold' ? thresholdValue : null,
      purchase_volume_threshold:
        thresholdField === 'purchase_volume_threshold' ? thresholdValue : null,
      period_threshold: details.rebate?.period_threshold,
      apply_to_all_hospitals: details.rebate?.apply_to_all_hospitals,
      hospitals_rebates: details.rebate?.hospitals_rebates,
      threshold_measured_in: thresholdMeasuredIn,
      is_tiered: details.rebate?.is_tiered ?? false
    }
  }

  return {
    rebate_type: details.rebate_type,
    ...rebate
  }
}

export const convertFormToRequestData = (
  values: IRebateConditionForm
): RebateDataRequest => {
  let rebate: IRebateCondition = { ...values }

  if (values.rebate_type === REBATE_CONDITIONS_TYPE.NO_REBATE) {
    rebate = null
  }

  if (values.rebate_type === REBATE_CONDITIONS_TYPE.STRAIGHT_REBATE) {
    rebate = {
      amount: values.amount,
      period_threshold: values.period_threshold
    }
  }

  if (values.rebate_type === REBATE_CONDITIONS_TYPE.GROWTH_REBATE) {
    rebate = {
      amount: values.amount,
      purchase_volume_threshold: values.purchase_volume_threshold,
      period_threshold: values.period_threshold
    }
  }

  if (values.rebate_type === REBATE_CONDITIONS_TYPE.MARKET_SHARE_REBATE) {
    const thresholdMeasuredIn =
      values.threshold_measured_in ?? MARKET_SHARE_THRESHOLD_MEASURED_IN
    const thresholdField = getMarketShareThresholdFieldName(thresholdMeasuredIn)

    rebate = {
      amount: values.amount,
      period_threshold: values.period_threshold,
      apply_to_all_hospitals: values.apply_to_all_hospitals,
      threshold_measured_in: thresholdMeasuredIn,
      is_tiered: values.is_tiered
    }
    if (values[thresholdField]) {
      rebate[thresholdField] = values[thresholdField]
    }
    if (!!values.hospitals_rebates?.length) {
      rebate.hospitals_rebates = values.hospitals_rebates
    }
  }

  return {
    rebate_type: values.rebate_type,
    rebate
  }
}

const formatTierDecimal = (value: number | string | null | undefined) => {
  if (value == null || value === '') return null

  const numericValue = Number(value)
  if (Number.isNaN(numericValue)) return null

  return numericValue.toFixed(2)
}

const toTierNumber = (value: number | string | null | undefined) => {
  if (value == null || value === '') return null

  const numericValue = Number(value)
  return Number.isNaN(numericValue) ? null : numericValue
}

export const convertTiersToRequest = (
  tiers: RebateTier[],
  thresholdMeasuredIn?: string
): { tiers: RebateTierRequest[] } => {
  const isPurchaseVolume =
    thresholdMeasuredIn === PURCHASE_VOLUME_THRESHOLD_MEASURED_IN

  return {
    tiers: tiers.map((tier) => ({
      uuid: tier.uuid ?? null,
      market_share_threshold: isPurchaseVolume
        ? null
        : formatTierDecimal(tier.threshold),
      purchase_volume_threshold: isPurchaseVolume
        ? formatTierDecimal(tier.threshold)
        : null,
      amount: formatTierDecimal(tier.amount)
    }))
  }
}

export const normalizeRebateTiers = (
  data:
    | RebateTierRequest[]
    | { tiers?: RebateTierRequest[]; results?: RebateTierRequest[] }
    | null
    | undefined,
  thresholdMeasuredIn?: string
): RebateTier[] => {
  const list = Array.isArray(data) ? data : data?.tiers ?? data?.results ?? []
  const isPurchaseVolume =
    thresholdMeasuredIn === PURCHASE_VOLUME_THRESHOLD_MEASURED_IN

  return list.map((tier) => ({
    uuid: tier?.uuid ?? null,
    threshold: toTierNumber(
      isPurchaseVolume
        ? tier?.purchase_volume_threshold
        : tier?.market_share_threshold
    ),
    amount: toTierNumber(tier?.amount)
  }))
}

export const isEmptyValue = (value: unknown): boolean =>
  value == null || value === ''

export const sequentialTierThresholdValidator = async (
  value,
  previousValue
) => {
  if (isEmptyValue(value) || isEmptyValue(previousValue)) {
    return Promise.resolve()
  }

  if (safeNumber(value) <= safeNumber(previousValue)) {
    return Promise.reject(new Error(THRESHOLD_LESS_THAN_PREVIOUS_TIER))
  }

  return Promise.resolve()
}

export const positiveNumberValidator = async (_, value) => {
  if (value > 0) {
    return Promise.resolve()
  }

  return Promise.reject(new Error('Threshold must be greater than 0'))
}

export const minRebateValueValidator = async (_, value) => {
  if (safeNumber(value) < 0.01) {
    return Promise.reject(new Error('Less than 0.01'))
  }

  return Promise.resolve()
}

export const percentageRebateValidator = async (_, value) => {
  await minRebateValueValidator(_, value)

  if (safeNumber(value) > 100) {
    return Promise.reject(new Error('More than 100'))
  }

  return Promise.resolve()
}

export const getRebateLabel = (rebate) =>
  PERIOD_OPTIONS.find((i) => i.value === rebate)?.label || ''
